/**
 * convertDateTimeToISO.js
 *
 * Replaces the hardcoded Bulgarian month-abbreviation parser that existed
 * inline in home.jsx and SearchResults.jsx.
 *
 * DateTimeSelector.getHeaderText() produces strings like:
 *   "14 яну от 08:00 до 20:00"   (bg)
 *   "14 jan from 08:00 to 20:00"  (en)
 *   "14 янв с 08:00 до 20:00"     (ru)
 *
 * The month token is always position [1] (after splitting on space) and is
 * the first 3 chars of t('months.<name>', { ns: 'datetime' }) lowercased.
 *
 * We build the reverse map at call time using the same i18n instance so it
 * automatically follows the active language.
 *
 * Usage:
 *   import { buildDateTimeISO } from '../utils/convertDateTimeToISO';
 *   const iso = buildDateTimeISO(dateTimeStr, t);   // t from useTranslation('datetime')
 */

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const PAD_MONTH = (idx) => String(idx + 1).padStart(2, '0');

const TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

/**
 * Parse the DateTimeSelector display string into structured params.
 *
 * DateTimeSelector.getHeaderText() emits, in every supported language:
 *   [0] day  [1] monthAbbrev  [2] from-word  [3] HH:MM  [4] to-word  [5] HH:MM
 * so positional token extraction is language-stable.
 *
 * @param {string} dateTimeStr
 * @param {Function} t  i18next t bound to the 'datetime' namespace
 * @returns {{date: string, start_time: string|null, end_time: string|null}|null}
 */
export function buildDateTimeParams(dateTimeStr, t) {
  if (!dateTimeStr) return null;

  const parts = dateTimeStr.split(' ');
  if (parts.length < 2) return null;

  const day = parts[0];
  const monthToken = parts[1].toLowerCase(); // e.g. "яну", "jan", "янв"

  // Build the abbrev → { padded month, month index } map from live translations
  const monthMap = {};
  MONTH_KEYS.forEach((key, idx) => {
    const full = t(`months.${key}`, { ns: 'datetime' });
    if (full) {
      const abbrev = full.slice(0, 3).toLowerCase();
      monthMap[abbrev] = { num: PAD_MONTH(idx), idx };
    }
  });

  const month = monthMap[monthToken];
  if (!month) return null;

  const dayNum = parseInt(day, 10);
  if (!Number.isFinite(dayNum) || dayNum < 1 || dayNum > 31) return null;

  // Year rollover: any month/day resolving to a date before today belongs to
  // next year. Covers all forward-picks past the year boundary, not just Dec→Jan.
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let year = now.getFullYear();
  const candidate = new Date(year, month.idx, dayNum);
  if (candidate < todayMidnight) year += 1;

  const date = `${year}-${month.num}-${String(dayNum).padStart(2, '0')}`;

  // Optional time range at positions [3] and [5]. Both must be well-formed and
  // ordered; otherwise both are dropped (no partial windows sent to backend).
  let start_time = null;
  let end_time = null;
  if (parts[3] && parts[5] && TIME_RE.test(parts[3]) && TIME_RE.test(parts[5]) && parts[3] < parts[5]) {
    start_time = parts[3];
    end_time = parts[5];
  }

  return { date, start_time, end_time };
}

/**
 * Backward-compatible date-only extractor. Prefer buildDateTimeParams for new callers.
 * @returns {string|null} ISO date "YYYY-MM-DD" or null
 */
export function buildDateTimeISO(dateTimeStr, t) {
  const params = buildDateTimeParams(dateTimeStr, t);
  return params ? params.date : null;
}